/**
 * Stripe payment-link service (goal 249) — thin `productData` wrapper.
 *
 * Replaces the axios calls to this app's own `/api/stripe/payment-link/*` and
 * `/api/stripe/billing-portal-link` Vercel proxies: the browser now calls the
 * ProductData Worker's `/api/customer/payments/*` routes directly with the
 * Better Auth JWT (see `@/lib/api/product-data-client`). Method names are kept
 * so the existing call sites change minimally.
 *
 * `single-payment`, `subscription-payment`, and `billing-portal-link` all
 * answer the standard envelope (`envelope({ url, sessionId? })`,
 * envelope-uniformity follow-up to Plan 07 — the first two were RAW
 * `c.json({ url, sessionId? }, 200)` before). `productData` unwraps it
 * transparently, so callers just get `{ url, sessionId? }` back.
 */
import type { FederalTaxFilingMode } from '@nt/federal-tax-rules'

import { productData } from '@/lib/api/product-data-client'

export class StripeApiService {
  static async SinglePayment(params: ISinglePaymentParams): Promise<PaymentLinkResponse> {
    return productData('/api/customer/payments/single-payment', {
      method: 'POST',
      body: JSON.stringify({
        purchase: params.purchase,
        companyId: params.companyId,
        taxYear: params.taxYear,
        filingMode: params.filingMode,
        successUrl: params.successUrl,
      }),
    })
  }

  static async SubscriptionPayment(params: ISubscriptionPaymentParams): Promise<PaymentLinkResponse> {
    return productData('/api/customer/payments/subscription-payment', {
      method: 'POST',
      body: JSON.stringify({
        priceId: params.priceId,
        companyId: params.companyId,
        isCredit: params.isCredit,
      }),
    })
  }

  static async BillingPortal(): Promise<PaymentLinkResponse> {
    return productData('/api/customer/payments/billing-portal-link')
  }

  static async BillingHealth(): Promise<BillingHealthResponse> {
    return productData('/api/customer/payments/billing-health')
  }
}

interface ISubscriptionPaymentParams {
  priceId: string
  /** The company the subscription is for. The Worker refuses a company the caller does not own. */
  companyId: string
  isCredit?: boolean
}

interface ISinglePaymentParams {
  purchase: 'federal_tax'
  companyId: string
  taxYear: number
  filingMode?: FederalTaxFilingMode
  successUrl?: string
}

interface PaymentLinkResponse {
  url: string
  sessionId?: string
}

export interface BillingHealthResponse {
  pastDue: Array<{
    subscriptionId: string
    serviceName: string
    amount: number
    currency: string
    nextAttemptDate: string | null
    hostedInvoiceUrl: string | null
  }>
  actionRequired: Array<{
    invoiceId: string
    serviceName: string
    hostedInvoiceUrl: string | null
  }>
  processing: Array<{ orderId: string; serviceName: string }>
}
